import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Grid} from 'src/design/atoms/Grid';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {
  DocumentReviewRequestStateEnum,
  DocumentReviewTypeEnum,
  DocumentStatusEnum,
  EntityIdentifierEnum,
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ViewField} from 'src/design/atoms/ViewField';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {FormViewFileListRow} from 'src/app/forms/FormViewFileListRow/FormViewFileListRow';
import {SidebarLinkDocumentManager} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {DocumentsBrowserShell} from 'src/app/documents/DocumentsBrowserShell';
import {toDocumentTreeNodeId} from 'src/app/documents/documentTreeBrowserState';
import {DocumentTreeModal} from 'src/pages/Modules/DocumentManagement/shared/DocumentTreeModal';
import {DocumentVersionsTable} from 'src/pages/Modules/DocumentManagement/ViewDocument/components/DocumentVersionsTable/DocumentVersionsTable';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {DocumentManagementViewType} from 'src/shared/constants/ViewTypes';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useDocumentAncestors} from 'src/shared/hooks/useDocumentAncestors';
import {useDocumentManagementBreadcrumbs} from 'src/shared/hooks/useDocumentManagementBreadcrumbs';
import {useDocumentReviewsFeature} from 'src/shared/hooks/useDocumentReviewsFeature';
import {useExtensionTabs} from 'src/shared/hooks/useExtensionTabs';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {
  useDeleteDocumentMutation,
  useGetDocumentQuery,
  useMoveDocumentMutation,
} from 'src/redux/queries/document';
import {
  useGetDocumentReviewRequestsQuery,
  useReRequestDocumentReviewMutation,
} from 'src/redux/queries/documentReview';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {documentTitle} from '../documentHelpers';
import {DocumentStatusPill} from '../documentReviewHelpers';
import {DocumentStateBanner} from './components/DocumentStateBanner';
import {DocumentReviewsTab} from './components/DocumentReviewsTab';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const formatDate = (date: any): string => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(date);
  }
};

export const ViewDocumentPage = () => {
  const navigate = useAppNavigate();
  const {nodeId, view} = useParams();
  const {setAlert} = useAlert();
  const [showMoveModal, setShowMoveModal] = useState<boolean>(false);

  const perms = useModulePermissions(ModuleEnum.Documents);
  const auditPerms = useAuditLogPermissions();
  const hasReviewsFeature = useDocumentReviewsFeature();
  const [isRequestingReReview, setIsRequestingReReview] = useState(false);

  useSetDocumentTitle(documentTitle);

  const validNodeId = nodeId && !isNaN(+nodeId) ? +nodeId : undefined;

  const {data: viewItem, isLoading} = useGetDocumentQuery(
    {id: validNodeId!},
    {
      skip: !validNodeId,
      // Review status flips via an async background job after approval/rejection,
      // so the cached document can be stale on return. Refetch on mount/arg change.
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (!validNodeId) {
      navigate(`${PathUtility.DocumentsManagerList(0)}`);
    }
  }, [validNodeId, navigate]);

  const documentId = viewItem?.id ?? 0;

  const extensionTabs = useExtensionTabs(ModuleEnum.Documents, documentId ?? 0);

  const [deleteDocument] = useDeleteDocumentMutation();
  const [moveDocument] = useMoveDocumentMutation();
  const [reRequestReview] = useReRequestDocumentReviewMutation();

  const isRejected = viewItem?.status === DocumentStatusEnum.Rejected;

  const {
    data: reviewRounds,
    isLoading: isLoadingReviewRounds,
    isFetching: isFetchingReviewRounds,
    refetch: refetchReviewRounds,
  } = useGetDocumentReviewRequestsQuery(
    {documentId},
    {skip: !hasReviewsFeature || !documentId}
  );

  const rejectedRound = reviewRounds?.find(
    round =>
      round.documentVersion === viewItem?.version &&
      round.reviewType === DocumentReviewTypeEnum.Approval &&
      round.state === DocumentReviewRequestStateEnum.Rejected
  );

  const handleRequestReReview = async () => {
    if (!viewItem) return;
    setIsRequestingReReview(true);
    try {
      await reRequestReview({
        documentId: viewItem.id,
        version: viewItem.version,
      }).unwrap();
      setAlert('Re-approval was successfully requested', 'success');
    } catch (error: unknown) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsRequestingReReview(false);
  };

  const handleEdit = () => {
    navigate(`${PathUtility.DocumentManagerEditFile(nodeId as string)}`);
  };

  const handleDelete = async () => {
    try {
      await deleteDocument(+nodeId!).unwrap();
      setAlert(`${viewItem?.name} was successfully deleted.`, 'success');
      navigate(PathUtility.DocumentsManagerList(viewItem?.parentId));
    } catch {
      setAlert('An error occurred deleting file.', 'error');
    }
  };

  const handleMove = () => {
    setShowMoveModal(true);
  };

  const handleMoveConfirm = async (newParentId: number | null) => {
    try {
      if (!viewItem || !newParentId) {
        setAlert('Document or parent folder not found.', 'error');
        return;
      }

      await moveDocument({
        id: viewItem.id,
        version: viewItem.version,
        newParentId: newParentId,
      }).unwrap();

      setAlert(`${viewItem?.name} was successfully moved.`, 'success');
      navigate(PathUtility.DocumentsManagerList(newParentId || 0));
    } catch {
      setAlert('An error occurred moving the document.', 'error');
    }

    setShowMoveModal(false);
  };

  const contentTab = (
    <Stack gap="md" data-testid="document-content-tab">
      <SectionCard title="Details" data-testid="section-details">
        <Box p="md">
          <Grid gutter="md">
            <Grid.Col span={12}>
              <ViewField
                label="Description"
                value={viewItem?.summaryDescription}
              />
            </Grid.Col>
          </Grid>
        </Box>
      </SectionCard>

      <SectionCard title="Attachment" data-testid="section-attachment">
        <Box p="md">
          <FormViewFileListRow
            label="File"
            value={viewItem?.attachmentURL as string}
          />
        </Box>
      </SectionCard>

      <SectionCard title="Metadata" data-testid="section-metadata">
        <Box p="md">
          <Grid gutter="md">
            <Grid.Col span={{base: 12, md: 4}}>
              <ViewField
                label="Created"
                value={formatDate(viewItem?.createdAt)}
              />
            </Grid.Col>
            <Grid.Col span={{base: 12, md: 4}}>
              <ViewField
                label="Last Updated"
                value={formatDate(viewItem?.updatedAt)}
              />
            </Grid.Col>
            <Grid.Col span={{base: 12, md: 4}}>
              <ViewField label="Version" value={viewItem?.version} />
            </Grid.Col>
          </Grid>
        </Box>
      </SectionCard>
    </Stack>
  );

  const {RenderedTabsComponent} = useUrlQueryTabV2<DocumentManagementViewType>({
    view: (view ?? 'content') as DocumentManagementViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: contentTab,
      },
      {
        view: 'versions',
        label: 'Versions',
        component: (
          <DocumentVersionsTable
            identifier="document-versions-table"
            documentId={documentId}
          />
        ),
      },
      {
        view: 'reviews',
        label: 'Reviews',
        hidden: !hasReviewsFeature,
        component: (
          <DocumentReviewsTab
            rounds={reviewRounds}
            isLoading={isLoadingReviewRounds}
            isFetching={isFetchingReviewRounds}
            onRefresh={refetchReviewRounds}
          />
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: !auditPerms.view,
        component: viewItem?.id ? (
          <AuditModule
            entityId={String(viewItem.id)}
            entityType={EntityIdentifierEnum.Document}
          />
        ) : null,
      },
      ...extensionTabs,
    ],
    onSelectView: view =>
      navigate(
        PathUtility.DocumentManagerViewFile(
          nodeId as string,
          view as DocumentManagementViewType
        ),
        {replace: true}
      ),
  });

  const statusPill = DocumentStatusPill(viewItem?.status);

  const ancestors = useDocumentAncestors(nodeId, viewItem?.parentId);

  const {breadcrumbs, fallbackTo} = useDocumentManagementBreadcrumbs({
    mode: 'view-document',
    nodeId: nodeId,
    parentId: viewItem?.parentId,
    nodeName: viewItem?.name,
    ancestors,
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.DocumentManagement}>
      <DocumentsBrowserShell selectedId={toDocumentTreeNodeId(nodeId)}>
        {!isLoading ? (
          <Stack gap="lg">
            <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />

            <PageHeader>
              <PageHeader.Title icon={SidebarLinkDocumentManager.icon}>
                {viewItem ? viewItem.name : 'Item not found'}
              </PageHeader.Title>

              {viewItem?.createdAt && (
                <PageHeader.Meta>
                  <PageHeader.MetaItem label="Created">
                    {formatDate(viewItem.createdAt)}
                  </PageHeader.MetaItem>
                  {viewItem.version && (
                    <PageHeader.MetaItem label="Version">
                      {String(viewItem.version)}
                    </PageHeader.MetaItem>
                  )}
                </PageHeader.Meta>
              )}

              {hasReviewsFeature && statusPill && (
                <PageHeader.Badges>
                  <PageHeader.Badge color={statusPill.color} variant="light">
                    {statusPill.text}
                  </PageHeader.Badge>
                </PageHeader.Badges>
              )}

              <PageHeader.Actions>
                {hasReviewsFeature && isRejected && perms.update && (
                  <PageHeader.Action
                    loading={isRequestingReReview}
                    onClick={handleRequestReReview}>
                    Request re-approval
                  </PageHeader.Action>
                )}
                {perms.update && (
                  <>
                    <PageHeader.Action variant="edit" onClick={handleEdit}>
                      Edit
                    </PageHeader.Action>
                    <PageHeader.Action onClick={handleMove}>
                      Move
                    </PageHeader.Action>
                  </>
                )}
                {perms.delete && (
                  <PageHeader.Action variant="delete" onClick={handleDelete}>
                    Delete
                  </PageHeader.Action>
                )}
              </PageHeader.Actions>
            </PageHeader>

            {hasReviewsFeature && (
              <DocumentStateBanner
                pendingVersion={viewItem?.pendingVersion}
                isRejected={isRejected}
                version={viewItem?.version}
                resolvedByName={rejectedRound?.resolvedByName}
                resolutionComment={rejectedRound?.resolutionComment}
                canRequestReReview={perms.update}
                onViewVersions={() =>
                  navigate(
                    PathUtility.DocumentManagerViewFile(
                      nodeId as string,
                      'versions'
                    ),
                    {replace: true}
                  )
                }
              />
            )}

            {RenderedTabsComponent}
          </Stack>
        ) : (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        )}
      </DocumentsBrowserShell>

      <DocumentTreeModal
        opened={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        onConfirm={handleMoveConfirm}
        title="Move Document"
        currentItemName={viewItem?.name}
        currentItemType="file"
        currentItemId={viewItem?.id}
      />
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.DocumentManagerViewFile(':nodeId', ':view'),
  element: <ViewDocumentPage />,
  module: ModuleEnum.Documents,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
